import { AnnualLeave, Arrays, Dates, HolidaysLogic, HolidayYear } from "@salaxy/core";

import { EditDialogResult, UiHelpers } from "../../services";

import { ListControllerBase, ListControllerBaseBindings } from "../bases";

class HolidayYearHolidaysControllerBindings extends ListControllerBaseBindings {
  /** If true, will format the table with class table-condensed. Later may add some other condensed formatting. */
  public condensed = "<";

  /** Date filter start value. Will be compared to period end date. */
  public filterStart = "<";

  /** Date filter end value. Will be compared to period start date. */
  public filterEnd = "<";
}

/**
 * Controls the planned holidays list (lomakirjanpito / lomakalenteri) for a selected holiday year.
 */
export class HolidayYearHolidaysController  extends ListControllerBase<HolidayYear, AnnualLeave> {

  /** Bindings for components that use this controller */
  public static bindings = new HolidayYearHolidaysControllerBindings();

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["UiHelpers"];

  /** If true, will format the table with class table-condensed. Later may add some other condensed formatting. */
  public condensed: boolean;

  /** Date filter start value. Will be applied to period start dates. */
  public filterStart: string;

  /** Date filter end value. Will be applied to period end dates. */
  public filterEnd: string;

  constructor(uiHelpers: UiHelpers) {
    super(uiHelpers);
  }

  /** List of items */
  public get list(): AnnualLeave[] {
    if (!this.parent || !this.parent.leaves || !this.parent.leaves.planned) {
      return [];
    }
    return this.parent.leaves.planned;
  }

  /**
   * Filter that is applied to the list when displayed.
   * This is done in view: Does not apply to list property, but does affect getTotalDays().
   * @param value Item in the list
   */
  public filter = (value: AnnualLeave) => {
    if (this.filterEnd && value.period.start > this.filterEnd) {
      return false;
    }
    if (this.filterStart && value.period.end < this.filterStart) {
      return false;
    }
    return true;
  };

  /** Creating of a new item. */
  public getBlank(): AnnualLeave {
    return {
      period: {
        start: Dates.getToday(),
        end: Dates.getToday(),
        daysCount: 1,
      },
    };
  }

  /** Template for edit UI that is shown in a modal dialog. */
  public getEditDialogTemplateUrl() {
    return "salaxy-components/worker/holidays/HolidayYearHolidaysEditDialog.html";
  }

  /** Logic for edit dialog. */
  public getEditDialogLogic() {
    return {
      /** Updates the workdays count */
      updatePeriodDays: (current: AnnualLeave) => {
        if (current.period.start > current.period.end) {
          current.period.end = current.period.start;
        }
        if (current.period.start && current.period.end) {
          if (current.period.days) {
            current.period.days = Dates.getVacationDays(current.period.start, current.period.end);
            current.period.daysCount = current.period.days.length;
          } else {
            current.period.daysCount = Dates.getVacationDays(current.period.start, current.period.end).length;
          }
        }
      },

      /** Sets the days array in period to enable days selection UI. */
      setDaysSelection: (period: AnnualLeave) => {
        period.period.days = Dates.getVacationDays(period.period.start, period.period.end);
        period.period.daysCount = period.period.days.length;
      },
      /**
       * For example, when parent year is 2024:
       * - minDate is 2024-01-01
       * - maxDate is 2025-04-30
       */
      minDate: Dates.getDate(this.parent.year, 1, 1),
      maxDate: Dates.getDate(this.parent.year + 1, 4, 30)
    };
  }

  /**
   * Shows the edit dialog.
   * @param item Annual leave to edit or string "new" for creating a new one.
   * @param isNew Optional way of specifying that the item is a new item (with default values, not yet added to the list).
   * If item is string "new", this parameter will have no effect (will always be true).
   */
  public showEditDialog(item: AnnualLeave | "new", isNew = false) {
    if (!item) {
      throw new Error("showEditDialog called without a parameter.");
    }
    if (item === "new") {
      isNew = true;
      item = this.getBlank();
    }

    const handleResult = (result: EditDialogResult<AnnualLeave>) => {
      if (result.action === "ok" || result.action === "ok-no-save" ) {
        if (isNew) {
          this.list.push(result.item);
        }
        if (( isNew || result.hasChanges) && result.action !== "ok-no-save") {
          this.commit();
        }
      } else if (result.action === "delete" || result.action === "delete-no-save") {
        if (!isNew) {
          this.delete(item as AnnualLeave);
          if (result.action !== "delete-no-save" ) {
            this.commit();
          }
        }
      }
    };

    this.uiHelpers.openEditDialog(this.getEditDialogTemplateUrl(), item, this.getEditDialogLogic()).then((result) => {
      /**
       * parent is currently used holiday year
       * Check if the holiday is outside the general holiday season (1.5.parent.year -30.4.parent.year + 1). Outside of general holiday season means 1.1.parent.year - 30.4.parent.year
       */

      if( (result.action === "ok" || result.action === "ok-no-save") && ( result.item.period.start >= Dates.getDate(this.parent.year,1,1) && result.item.period.end <= Dates.getDate(this.parent.year,4,30))) {
        this.uiHelpers.showConfirm("SALAXY.NG1.WorkerHolidaysComponent.HolidayYearHolidaysEditDialog.dialogHeading", "SALAXY.NG1.WorkerHolidaysComponent.HolidayYearHolidaysEditDialog.dialogText")
        .then((confirmResult: boolean) => {
          if (confirmResult) {
            handleResult(result);
          }
        });
      } else {
        handleResult(result);
      }
    });
  }

  /** Gets the description text for the planned holiday. */
  public getDescription(row: AnnualLeave) {
    if (row.notes) {
      return row.notes;
    }
    return row.period.start < `${this.parent.year}-10-1` ? "Kesäloma" : "Talviloma";
  }

  /** Gets a total days calculation for different types. */
  public getTotalDays(type: "all" | "summer" | "winter" | "holidaysSaldoEnd" | "holidaysSaldoStart" | "filtered" = "all") {
    // HACK: jos periodi menee yli filterin tästä tulee väärä tulos (alku ennen filtterin alkua, loppu filtterin alun jälkeen).
    if (type === "filtered") {
      return Arrays.sum(this.list.filter((x) => this.filter(x)), (x) => x.period.daysCount);
    }
    if (this.parent) {
      return HolidaysLogic.getPlannedLeavesCount(this.parent, type);
    }
    return null;
  }
}
